import React, { useState } from "react";
import { toast, Slide } from "react-toastify";
import {
  connectSerial,
  sendCommand,
  disconnectSerial,
  connectBluetooth,
  disconnectBluetooth,
  connectWiFi,
  disconnectWiFi,
} from "../../utils/deviceConnectionManager";
import { connectSerial as sharedConnectSerial } from "../../utils/deviceConnectionManager";
import "react-toastify/dist/ReactToastify.css";

console.log("🧠 ConnectionModal loaded...");

if (typeof window !== "undefined") {
  window.isSerialConnected = false;
  window.isBluetoothConnected = false;
  window.isWiFiConnected = false;
}

export default function ConnectionModal({
  isOpen,
  onClose,
  onPeripheralConnected,
  onBluetoothPortDisconnected,
  onRequestCloseConnect,
}) {
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [isWiFiConnected, setIsWiFiConnected] = useState(false);
  const [esp32IP, setEsp32IP] = useState(null);
  const [isSerialConnected, setIsSerialConnected] = useState(false);
  const [deviceName, setDeviceName] = useState("");

  // ============================================================
  // 🧲 BLUETOOTH
  // ============================================================
  const connectToESP32 = async () => {
    try {
      // Check if Serial is already connected
      if (window.isSerialConnected) {
        toast.error("USB Serial is already connected. Please disconnect USB first.", {
          position: "bottom-right",
          transition: Slide,
          autoClose: 4000,
          closeButton: false,
        });
        return;
      }

      console.log("🔵 Attempting Bluetooth connection...");

      // Connect via Bluetooth (using static import)
      const result = await connectBluetooth();

      console.log("✅ Connection result:", result);

      // Update state IMMEDIATELY
      setIsBluetoothConnected(true);
      setBluetoothDevice(result.device);
      setDeviceName(result.device);

      // Update window globals
      window.isBluetoothConnected = true;
      window.isSerialConnected = false;

      console.log("State updated:", {
        isBluetoothConnected: true,
        deviceName: result.device,
        windowFlag: window.isBluetoothConnected
      });

      // Success toast
      toast.success(`✅ Bluetooth connected: ${result.device}`, {
        position: "bottom-right",
        transition: Slide,
        autoClose: 3000,
        closeButton: false,
      });

      console.log("✅ Bluetooth connection successful!");

      // 🔴 NEW: auto close drop-up on success
      if (onClose) onClose();

    } catch (error) {
      console.error("❌ Bluetooth connection failed:", error);

      setIsBluetoothConnected(false);
      setBluetoothDevice(null);
      setDeviceName("");
      window.isBluetoothConnected = false;

      // Error toast
      toast.error(`❌ Bluetooth connection failed: ${error.message}`, {
        position: "bottom-right",
        transition: Slide,
        autoClose: 4000,
        closeButton: false,
      });
    }
  };

  const disconnectFromESP32 = async () => {
    if (bluetoothDevice?.gatt?.connected) {
      bluetoothDevice.gatt.disconnect();
      setBluetoothDevice(null);
      setIsBluetoothConnected(false);
      window.isBluetoothConnected = false;
      setDeviceName("");
      toast.info("🔌 Bluetooth disconnected.", {
        position: "top-right",
        transition: Slide,
        autoClose: 2000,
        closeButton: false,
        toastId: "bluetooth-disconnected",
      });

    }
  };

  const disconnectESP32Bluetooth = async () => {
    try {
      console.log("🔵 Disconnecting Bluetooth...");

      // Disconnect (now using static import!)
      await disconnectBluetooth();

      // Update state
      setIsBluetoothConnected(false);
      setBluetoothDevice(null);
      setDeviceName("");
      window.isBluetoothConnected = false;

      // Success toast
      toast.info("🔌 Bluetooth disconnected", {
        position: "bottom-right",
        transition: Slide,
        autoClose: 3000,
        closeButton: false,
      });

      console.log("✅ Bluetooth disconnected successfully!");

    } catch (error) {
      console.error("❌ Bluetooth disconnect failed:", error);

      toast.error("❌ Bluetooth disconnect failed", {
        position: "bottom-right",
        transition: Slide,
        autoClose: 4000,
        closeButton: false,
      });
    }
  };

  const handleBluetoothConnection = async () => {
    console.log('🔵🔵🔵 handleBluetoothConnection STARTED');
    console.log('Current state:', isBluetoothConnected);

    try {
      if (!isBluetoothConnected) {
        console.log('📍 About to call connectToESP32()');
        await connectToESP32();
        console.log('📍 connectToESP32() returned');

        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.log('📍 About to disconnect');
        await disconnectESP32Bluetooth();
        console.log('📍 Disconnect complete');

        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    } catch (error) {
      console.error('🔴🔴🔴 handleBluetoothConnection ERROR:', error);
      console.error('Error stack:', error.stack);
    }

    console.log('🔵🔵🔵 handleBluetoothConnection ENDED');
  };

  // ============================================================
  // 📡 WIFI
  // ============================================================
  const handleWiFiConnect = async () => {
    // Ask IP manually every time
    const ip = prompt("Enter ESP32 IP address:", localStorage.getItem("esp32IP") || "192.168.31.128");
    if (!ip) return;

    try {
      // Test connection by sending PING to /control
      const res = await fetch(`http://${ip}/control`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: "PING",
        mode: "no-cors"
      });

      // No-cors returns undefined response but request succeeded if no error occurred
      localStorage.setItem("esp32IP", ip);
      window.esp32IP = ip;
      window.isWiFiConnected = true;
      setIsWiFiConnected(true);
      setDeviceName(`WiFi (${ip})`);

      toast.success("📡 WiFi Connected!", { autoClose: 1200 });

      // 🔴 NEW: auto close drop-up on success
      if (onClose) onClose();

    } catch (err) {
      toast.error("❌ WiFi connection FAILED", { autoClose: 1500 });
      console.error("WiFi test error:", err);
    }
  };

  const handleWiFiDisconnect = async () => {  

    await turnOffAllPinsWiFi(); // <-- executes before disconnect

    window.isWiFiConnected = false;
    window.esp32IP = null;
    localStorage.removeItem("esp32IP");

    setIsWiFiConnected(false);
    setDeviceName("");

    toast.info("🔌 WiFi Disconnected & LED OFF", { autoClose: 900 });
  };


  const handleWiFiToggle = async () => {
    if (isWiFiConnected) await handleWiFiDisconnect();
    else await handleWiFiConnect();
  };

  // ============================================================
  // 🧩 SERIAL (USB)
  // ============================================================
  const handleSerialConnection = async () => {
    console.log("🔹 Serial Connect button clicked!");

    // ✨ NEW: Check if Bluetooth is already connected
    if (window.isBluetoothConnected) {
      toast.error("Bluetooth is already connected. Please disconnect Bluetooth first.", {
        position: "top-right",
        transition: Slide,
        autoClose: 4000,
        closeButton: false,
        toastId: "bluetooth-already-connected",
      });
      return; // Exit early, don't connect Serial
    }

    if (!isSerialConnected) {
      const result = await connectSerial();
      if (result.success) {
        console.log("✅ Shared serial connection established");
        setIsSerialConnected(true);
        setDeviceName("ESP32 Serial");
        window.isSerialConnected = true;
        toast.success("✅ Serial connected successfully!", {
          position: "top-right",
          transition: Slide,
          autoClose: 2000,
          closeButton: false,
          toastId: "serial-connected",
        });

        // 🔴 NEW: auto close drop-up on success
        if (onClose) onClose();

      } else {
        console.error("❌ Serial connection failed:", result.error);
        toast.error("❌ Serial connection failed!", {
          position: "top-right",
          transition: Slide,
          autoClose: 2000,
          closeButton: false,
          toastId: "serial-error",
        });

      }
    } else {
      await disconnectSerial();
      setIsSerialConnected(false);
      window.isSerialConnected = false;
      setDeviceName("");
      toast.info("🔌 Serial disconnected", {
        position: "top-right",
        transition: Slide,
        autoClose: 2000,
        closeButton: false,
        toastId: "serial-disconnected",
      });

    }
  };


  // ============================================================
  // 🖼️ UI Rendering  (DROP-UP instead of full overlay)
  // ============================================================
  if (!isOpen) return null;

  return (
    <div
      className="connection-dropup-panel"
      style={{
        position: "fixed",
        right: "32px",
        bottom: "100px",     // ~86px footer + small gap
        background: "#ffffff",
        borderRadius: "10px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        zIndex: 1000,
        minWidth: "220px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h2 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>🔗 Connect to Device</h2>

      <button onClick={handleBluetoothConnection}>
        {isBluetoothConnected
          ? `Disconnect Bluetooth (${deviceName})`
          : "Connect Bluetooth"}
      </button>

      <button onClick={handleWiFiToggle}>
        {isWiFiConnected
          ? `Disconnect WiFi (${deviceName})`
          : "Connect WiFi"}
      </button>

      <button onClick={handleSerialConnection}>
        {isSerialConnected
          ? `Disconnect Serial (${deviceName})`
          : "Connect Serial"}
      </button>

      <button onClick={onClose}>Close</button>
    </div>
  );
}

// ============================================================
// ✅ Exported Utility Scanners (used by Toolbar.js)
// ============================================================

export async function scanBluetoothDevices(setList) {
  try {
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: ["battery_service"],
    });
    setList((prev) => [...new Set([...prev, device.name || "Unknown Device"])]);
  } catch (err) {
    console.warn("Bluetooth scan cancelled or failed:", err);
  }
}

export async function scanWiFiDevices(setList) {
  try {
    const storedIPs = JSON.parse(localStorage.getItem("knownWiFiDevices") || "[]");
    setList(storedIPs.length ? storedIPs : ["192.168.4.1 (Default ESP32)"]);
  } catch (err) {
    console.warn("WiFi scan failed:", err);
  }
}

export async function scanSerialDevices(setDevices) {
  console.log("⚙️ scanSerialDevices() CALLED");

  try {
    const result = await sharedConnectSerial(); // ✅  use shared manager
    if (result.success) {
      console.log("✅  ESP32 connected via shared manager");
      setDevices(["✅  ESP32 Connected via Serial"]);
    } else {
      console.warn("⚠️ Could not connect to ESP32:", result.error);
      setDevices(["❌ Failed to connect ESP32"]);
    }
  } catch (err) {
    console.error("⚠️ Serial scan failed:", err);
    setDevices(["❌ No valid ESP32 devices found"]);
  }
}

export async function disconnectAll() {
  try {
    if (window.isWiFiConnected && window.esp32IP) {
      await fetch(`http://${window.esp32IP}/disconnect`, { method: "POST" });
      window.isWiFiConnected = false;
      window.esp32IP = null;
    }

    if (window.serialPort) {
      await disconnectSerial();
      window.serialPort = null;
      window.isSerialConnected = false;
    }

    if (window.bluetoothCharacteristic) {
      const device = window.bluetoothCharacteristic.service.device;
      if (device?.gatt?.connected) device.gatt.disconnect();
      window.bluetoothCharacteristic = null;
      window.isBluetoothConnected = false;
    }

    console.log("✅ All connections disconnected");
    return true;
  } catch (err) {
    console.error("❌ disconnectAll() failed:", err);
    return false;
  }
}

async function turnOffAllPinsWiFi() {
  if (!window.esp32IP) return;
  try {
    await fetch(`http://${window.esp32IP}/control`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: "SET_DIGITAL_OUTPUT 2 LOW",   // turn LED off
      mode: "no-cors"
    });
    console.log("💡 LED forced OFF on WiFi disconnect");
  } catch(e) {
    console.warn("⚠️ Could not send LED OFF before disconnect", e);
  }
}
